import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import API_BASE_URL from '../config/api';
import { useUser } from '../context/UserContext';
import { Helmet } from '../utils/helmet';

// Minimal inline styles so this page works without requiring a CSS module.
const styles = {
  page: { maxWidth: 980, margin: '0 auto', padding: '1.25rem' },
  headerRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 16 },
  title: { margin: 0 },
  card: {
    border: '1px solid var(--border-color, rgba(0,0,0,0.12))',
    borderRadius: 12,
    padding: 16,
    background: 'var(--bg-color, #fff)',
  },
  grid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 },
  row: { display: 'flex', flexDirection: 'column', gap: 6 },
  label: { fontWeight: 700, fontSize: 12, opacity: 0.85 },
  input: { padding: '10px 12px', borderRadius: 10, border: '1px solid rgba(0,0,0,0.18)' },
  textarea: { padding: '10px 12px', borderRadius: 10, border: '1px solid rgba(0,0,0,0.18)', minHeight: 140 },
  select: { padding: '10px 12px', borderRadius: 10, border: '1px solid rgba(0,0,0,0.18)' },
  actions: { display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 14, flexWrap: 'wrap' },
  btn: { padding: '10px 14px', borderRadius: 10, border: '1px solid rgba(0,0,0,0.18)', cursor: 'pointer' },
  btnPrimary: {
    padding: '10px 14px',
    borderRadius: 10,
    border: '1px solid rgba(0,0,0,0.18)',
    cursor: 'pointer',
    background: 'var(--color4, #27667b)',
    color: 'var(--bg-color, #fff)',
    fontWeight: 700,
  },
  error: { color: 'crimson', marginTop: 10, whiteSpace: 'pre-wrap' },
  help: { fontSize: 12, opacity: 0.8, marginTop: 6, lineHeight: 1.4 },
};

export default function EditMountainPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser } = useUser();

  const isEditorOrAdmin = useMemo(
    () => !!currentUser?.admin || !!currentUser?.editor,
    [currentUser?.admin, currentUser?.editor]
  );

  const preloadedMountain = location.state?.mountain ?? null;

  const [loading, setLoading] = useState({ page: true, saving: false });
  const [error, setError] = useState('');

  const [name, setName] = useState('');
  const [height, setHeight] = useState('');
  const [country, setCountry] = useState('');
  const [difficulty, setDifficulty] = useState('');
  const [gearProfile, setGearProfile] = useState('');
  const [mountainRange, setMountainRange] = useState('');
  const [seasonStart, setSeasonStart] = useState('');
  const [seasonEnd, setSeasonEnd] = useState('');
  const [description, setDescription] = useState('');

  // Authz gate (client-side). Backend also enforces editor/admin.
  useEffect(() => {
    if (!currentUser) {
      navigate('/login');
      return;
    }
    if (!isEditorOrAdmin) {
      navigate('/mountains/' + id);
    }
  }, [currentUser, isEditorOrAdmin, navigate, id]);

  // Load mountain data (from navigation state if present, otherwise fetch).
  useEffect(() => {
    let isMounted = true;

    const applyMountainToForm = (m) => {
      setName(m?.name ?? '');
      setHeight(m?.height != null ? String(m.height) : '');
      setCountry(m?.country ?? '');
      setDifficulty(m?.difficulty ?? '');
      setGearProfile(m?.gearProfile ?? '');
      setMountainRange(m?.mountainRange ?? '');
      setSeasonStart(m?.season?.startMonth ?? '');
      setSeasonEnd(m?.season?.endMonth ?? '');
      setDescription(m?.description ?? '');
    };

    const fetchMountain = async () => {
      setLoading((prev) => ({ ...prev, page: true }));
      setError('');

      try {
        if (preloadedMountain) {
          applyMountainToForm(preloadedMountain);
          return;
        }

        const res = await fetch(`${API_BASE_URL}/api/mountains/${id}`);
        const data = await res.json().catch(() => ({}));

        if (!res.ok || !data.success) {
          throw new Error(data.message || `Failed to fetch mountain (${res.status})`);
        }

        if (isMounted) applyMountainToForm(data.data);
      } catch (e) {
        if (isMounted) setError(e?.message || 'Failed to load mountain.');
      } finally {
        if (isMounted) setLoading((prev) => ({ ...prev, page: false }));
      }
    };

    fetchMountain();

    return () => {
      isMounted = false;
    };
  }, [id, preloadedMountain]);

  const handleSave = async (e) => {
    e.preventDefault();
    if (!currentUser?.token) return;

    setLoading((prev) => ({ ...prev, saving: true }));
    setError('');

    try {
      // Keep payload aligned with backend MountainUpdater.updateMountain()
      const payload = {
        name: (name || '').trim(),
        height,
        country: (country || '').trim(),
        difficulty: (difficulty || '').trim(),
        gearProfile,
        mountainRange: (mountainRange || '').trim(),
        seasonStart: (seasonStart || '').trim(),
        seasonEnd: (seasonEnd || '').trim(),
        description: (description || '').trim(),
      };

      const res = await fetch(`${API_BASE_URL}/api/mountains/${id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${currentUser.token}`,
        },
        body: JSON.stringify(payload),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) {
        throw new Error(data.message || `Save failed (${res.status})`);
      }

      navigate(`/mountains/${id}`, { state: { mountain: data.data } });
    } catch (e) {
      setError(e?.message || 'Failed to save changes.');
    } finally {
      setLoading((prev) => ({ ...prev, saving: false }));
    }
  };

  const pageTitle = name ? `Edit ${name} | PeaksOnFoot` : 'Edit Mountain | PeaksOnFoot';

  if (!currentUser || !isEditorOrAdmin) {
    // Navigation effect will redirect; avoid flashing the form.
    return null;
  }

  return (
    <>
      <style>{`
        @media (max-width: 768px) and (orientation: portrait) {
          .editMountainPageRoot {
            padding-top: 3rem !important;
          }
        }
      `}</style>
      <Helmet>
        <title>{pageTitle}</title>
        <meta
          name="description"
          content="Edit mountain details, difficulty, and imagery on PeaksOnFoot."
        />
      </Helmet>
      <div style={styles.page} className="editMountainPageRoot">
      <div style={styles.headerRow}>
        <Link to={`/mountains/${id}`} className="backButton">
          ← Back to mountain
        </Link>
        <h1 style={styles.title}>Edit mountain</h1>
      </div>

      <div style={styles.card}>
        {loading.page ? (
          <p>Loading…</p>
        ) : (
          <form onSubmit={handleSave}>
            <div style={styles.grid}>
              <div style={styles.row}>
                <label style={styles.label} htmlFor="name">
                  Name
                </label>
                <input
                  id="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  style={styles.input}
                  placeholder="Mountain name"
                  required
                />
              </div>

              <div style={styles.row}>
                <label style={styles.label} htmlFor="height">
                  Height (m)
                </label>
                <input
                  id="height"
                  value={height}
                  onChange={(e) => setHeight(e.target.value)}
                  style={styles.input}
                  placeholder="e.g. 4810"
                  inputMode="numeric"
                />
              </div>

              <div style={styles.row}>
                <label style={styles.label} htmlFor="country">
                  Country
                </label>
                <input
                  id="country"
                  value={country}
                  onChange={(e) => setCountry(e.target.value)}
                  style={styles.input}
                  placeholder="e.g. Austria"
                  required
                />
              </div>

              <div style={styles.row}>
                <label style={styles.label} htmlFor="range">
                  Mountain range
                </label>
                <input
                  id="range"
                  value={mountainRange}
                  onChange={(e) => setMountainRange(e.target.value)}
                  style={styles.input}
                  placeholder="e.g. Alps"
                />
              </div>

              <div style={styles.row}>
                <label style={styles.label} htmlFor="difficulty">
                  Difficulty
                </label>
                <input
                  id="difficulty"
                  value={difficulty}
                  onChange={(e) => setDifficulty(e.target.value)}
                  style={styles.input}
                  placeholder="e.g. F, PD, AD, ..."
                />
              </div>

              <div style={styles.row}>
                <label style={styles.label} htmlFor="gearProfile">
                  Gear profile
                </label>
                <select
                  id="gearProfile"
                  value={gearProfile}
                  onChange={(e) => setGearProfile(e.target.value)}
                  style={styles.select}
                >
                  <option value="">(unset)</option>
                  <option value="hiking">Hiking – no technical gear</option>
                  <option value="easy-alpinism">Easy alpinism – basic alpine kit</option>
                  <option value="hard-alpinism">Hard alpinism – full technical kit</option>
                  <option value="himalayan">Himalayan / expedition – ascender & high-altitude kit</option>
                </select>
              </div>

              <div style={styles.row}>
                <label style={styles.label} htmlFor="seasonStart">
                  Season start
                </label>
                <input
                  id="seasonStart"
                  value={seasonStart}
                  onChange={(e) => setSeasonStart(e.target.value)}
                  style={styles.input}
                  placeholder="e.g. May"
                />
              </div>

              <div style={styles.row}>
                <label style={styles.label} htmlFor="seasonEnd">
                  Season end
                </label>
                <input
                  id="seasonEnd"
                  value={seasonEnd}
                  onChange={(e) => setSeasonEnd(e.target.value)}
                  style={styles.input}
                  placeholder="e.g. Sep"
                />
              </div>
            </div>

            <div style={{ ...styles.row, marginTop: 12 }}>
              <label style={styles.label} htmlFor="description">
                Description
              </label>
              <textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value.slice(0, 1500))}
                style={styles.textarea}
                placeholder="Write a short description…"
                maxLength="1500"
              />
              <div style={{ fontSize: '0.85rem', color: 'var(--text-color)', marginTop: '0.25rem', opacity: 0.7 }}>
                {description.length}/1500 characters
              </div>
              <div style={styles.help}>
                Only editors/admins can edit mountains. Changes are saved via{' '}
                <code>/api/mountains/:id</code> (PATCH).
              </div>
            </div>

            {error && <div style={styles.error}>{error}</div>}

            <div style={styles.actions}>
              <button type="button" style={styles.btn} onClick={() => navigate(`/mountains/${id}`)}>
                Cancel
              </button>
              <button type="submit" style={styles.btnPrimary} disabled={loading.saving}>
                {loading.saving ? 'Saving…' : 'Save changes'}
              </button>
            </div>
          </form>
        )}
      </div>
      </div>
    </>
  );
}
